import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { PageHeader } from '../components/PageHeader';
import {
  useBupotIssued,
  useBupotReceived,
  useClients,
  useCreateBupotReceived,
  useCreateSalaryRecord,
  useCreateTaxPayment,
  useMarkSalaryPaid,
  useMarkTaxPaymentPaid,
  usePeople,
  usePeredaranBruto,
  useSalaryRecords,
  useTaxPayments,
  useUpdateBupotIssued,
} from '../hooks/useOps';
import { opsApi } from '../api/client';
import type {
  BupotReceivedType,
  SalaryRecordType,
  TaxPayment,
  TaxPaymentType,
} from '../api/types';
import { formatDate, formatIDR, humanizeStatus } from '../lib/format';

const TAX_PAYMENT_TYPES: TaxPaymentType[] = [
  'final_05_self',
  'final_05_potput',
  'pph21',
  'pph23',
  'ppn',
  'other',
];

function currentMasa(): string {
  return new Date().toISOString().slice(0, 7);
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function TaxClosePage() {
  const [masa, setMasa] = useState(currentMasa());
  const year = Number(masa.slice(0, 4));

  const { data: dpb, error: dpbError } = usePeredaranBruto(year);
  const { data: payments } = useTaxPayments({ masa });
  const { data: received } = useBupotReceived({ masa });
  const { data: issued } = useBupotIssued({ masa });
  const { data: salary } = useSalaryRecords({ masa });
  const { data: clients } = useClients();
  const { data: people } = usePeople();

  const createPayment = useCreateTaxPayment();
  const markPaid = useMarkTaxPaymentPaid();
  const createBupot = useCreateBupotReceived();
  const updateIssued = useUpdateBupotIssued();
  const createSalary = useCreateSalaryRecord();
  const markSalaryPaid = useMarkSalaryPaid();

  const [formError, setFormError] = useState<string | null>(null);

  // New tax payment dialog.
  const [payOpen, setPayOpen] = useState(false);
  const [payType, setPayType] = useState<TaxPaymentType>('final_05_self');
  const [payAmount, setPayAmount] = useState(0);

  // Mark-paid (NTPN) dialog.
  const [ntpnFor, setNtpnFor] = useState<TaxPayment | null>(null);
  const [ntpn, setNtpn] = useState('');
  const [paidAt, setPaidAt] = useState(today());

  // Bupot received dialog.
  const [bupotOpen, setBupotOpen] = useState(false);
  const [bClient, setBClient] = useState('');
  const [bType, setBType] = useState<BupotReceivedType>('final_05');
  const [bDoc, setBDoc] = useState('');
  const [bDpp, setBDpp] = useState(0);
  const [bReceivedAt, setBReceivedAt] = useState(today());

  // Salary record dialog.
  const [salaryOpen, setSalaryOpen] = useState(false);
  const [sPerson, setSPerson] = useState('');
  const [sType, setSType] = useState<SalaryRecordType>('employee_monthly');
  const [sBruto, setSBruto] = useState(0);
  const [sPtkp, setSPtkp] = useState('');

  const dpbRow = dpb?.rows.find((r) => r.masa === masa);

  const downloadRunbook = async () => {
    const blob = await opsApi.taxRunbookPdf(masa);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `runbook-pajak-${masa}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const run = async (fn: () => Promise<unknown>, close?: () => void) => {
    try {
      setFormError(null);
      await fn();
      close?.();
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Tax close"
        action={
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              type="month"
              size="small"
              value={masa}
              onChange={(e) => setMasa(e.target.value)}
            />
            <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={downloadRunbook}>
              Runbook PDF
            </Button>
          </Stack>
        }
      />
      {dpbError && <Alert severity="error" sx={{ mb: 2 }}>{(dpbError as Error).message}</Alert>}
      {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>{formError}</Alert>}

      {/* ── 0.5% final self-deposit for the masa ── */}
      {dpbRow && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            PPh Final 0,5% — masa {masa} {dpbRow.provisional && <Chip size="small" label="provisional" sx={{ ml: 1 }} />}
          </Typography>
          <Stack direction="row" gap={4} flexWrap="wrap">
            <div>
              <Typography variant="caption" color="text.secondary">Omzet (GL 4100)</Typography>
              <Typography>{formatIDR(dpbRow.omzet)}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Withheld by clients (DPP)</Typography>
              <Typography>({formatIDR(dpbRow.withheldDpp)})</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Self-deposit base</Typography>
              <Typography>{formatIDR(dpbRow.selfDepositBase)}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Setor 0,5% (KAP-KJS 411128-420)</Typography>
              <Typography variant="h6">{formatIDR(dpbRow.finalPphDue)}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Deposit</Typography>
              <Typography>
                {dpbRow.payment
                  ? dpbRow.payment.ntpn
                    ? `NTPN ${dpbRow.payment.ntpn}`
                    : humanizeStatus(dpbRow.payment.status)
                  : '—'}
              </Typography>
            </div>
          </Stack>
        </Paper>
      )}

      {/* ── Daftar Peredaran Bruto (year) ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Daftar Peredaran Bruto {year}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Masa</TableCell>
              <TableCell align="right">Omzet</TableCell>
              <TableCell align="right">Withheld DPP</TableCell>
              <TableCell align="right">Self base</TableCell>
              <TableCell align="right">0,5% due</TableCell>
              <TableCell>NTPN</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(dpb?.rows ?? []).map((r) => (
              <TableRow key={r.masa} selected={r.masa === masa}>
                <TableCell>{r.masa}</TableCell>
                <TableCell align="right">{formatIDR(r.omzet)}</TableCell>
                <TableCell align="right">{formatIDR(r.withheldDpp)}</TableCell>
                <TableCell align="right">{formatIDR(r.selfDepositBase)}</TableCell>
                <TableCell align="right">{formatIDR(r.finalPphDue)}</TableCell>
                <TableCell>{r.payment?.ntpn ?? '—'}</TableCell>
              </TableRow>
            ))}
            {dpb && (
              <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{formatIDR(dpb.totals.omzet)}</TableCell>
                <TableCell align="right">{formatIDR(dpb.totals.withheldDpp)}</TableCell>
                <TableCell align="right">{formatIDR(dpb.totals.selfDepositBase)}</TableCell>
                <TableCell align="right">{formatIDR(dpb.totals.finalPphDue)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Tax payments (setoran) ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">Setoran pajak masa {masa}</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setPayOpen(true)}>
            Draft deposit
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>KAP-KJS</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>NTPN</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(payments ?? []).map((p) => (
              <TableRow key={p.id}>
                <TableCell>{humanizeStatus(p.taxType)}</TableCell>
                <TableCell>{p.kapKjs}</TableCell>
                <TableCell align="right">{formatIDR(p.amount)}</TableCell>
                <TableCell>
                  <Chip size="small" label={p.status} color={p.status === 'paid' ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>{p.ntpn ?? '—'}</TableCell>
                <TableCell>
                  {p.status === 'draft' && (
                    <Button size="small" onClick={() => { setNtpnFor(p); setNtpn(''); setPaidAt(today()); }}>
                      Mark paid
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(payments ?? []).length === 0 && (
              <TableRow><TableCell colSpan={6}><Typography color="text.secondary" variant="body2">No deposits recorded for this masa yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Bupot received ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">Bupot diterima dari klien — masa {masa}</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setBupotOpen(true)}>
            Record bupot
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Doc number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">DPP</TableCell>
              <TableCell align="right">Withheld</TableCell>
              <TableCell>Received</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(received ?? []).map((b) => (
              <TableRow key={b.id}>
                <TableCell>{b.docNumber}</TableCell>
                <TableCell>{humanizeStatus(b.taxType)}</TableCell>
                <TableCell align="right">{formatIDR(b.dpp)}</TableCell>
                <TableCell align="right">{formatIDR(b.amount)}</TableCell>
                <TableCell>{formatDate(b.receivedAt)}</TableCell>
              </TableRow>
            ))}
            {(received ?? []).length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography color="text.secondary" variant="body2">None yet — chase clients that withhold for their bukti potong.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Bupot issued (checklist) ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Bupot yang harus diterbitkan (Coretax) — masa {masa}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Counterparty</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>KOP</TableCell>
              <TableCell align="right">DPP</TableCell>
              <TableCell align="right">Withheld</TableCell>
              <TableCell>Doc number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(issued ?? []).map((b) => (
              <TableRow key={b.id}>
                <TableCell>{b.counterpartyName}</TableCell>
                <TableCell>{humanizeStatus(b.taxType)}</TableCell>
                <TableCell>{b.kop ?? '—'}</TableCell>
                <TableCell align="right">{formatIDR(b.dpp)}</TableCell>
                <TableCell align="right">{formatIDR(b.amount)}</TableCell>
                <TableCell>
                  {b.docNumber ?? (
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => {
                        const doc = window.prompt('Nomor bukti potong dari Coretax:');
                        if (doc) {
                          void run(() =>
                            updateIssued.mutateAsync({ id: b.id, body: { docNumber: doc, issuedAt: today() } }),
                          );
                        }
                      }}
                    >
                      Belum terbit — isi nomor
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(issued ?? []).length === 0 && (
              <TableRow><TableCell colSpan={6}><Typography color="text.secondary" variant="body2">No vendor/PPh 21 withholding this masa.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ── PPh 21 salary records ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">PPh 21 — gaji/honor masa {masa}</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setSalaryOpen(true)}>
            New record
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Person</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Bruto</TableCell>
              <TableCell align="right">PPh 21</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(salary ?? []).map((s) => (
              <TableRow key={s.id}>
                <TableCell>{s.personName}</TableCell>
                <TableCell>{humanizeStatus(s.type)}</TableCell>
                <TableCell align="right">{formatIDR(s.bruto)}</TableCell>
                <TableCell align="right">{formatIDR(s.pph21Amount)}</TableCell>
                <TableCell align="right">{formatIDR(s.netPaid)}</TableCell>
                <TableCell>
                  <Chip size="small" label={s.status} color={s.status === 'paid' ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>
                  {s.status === 'draft' && (
                    <Button
                      size="small"
                      onClick={() => void run(() => markSalaryPaid.mutateAsync({ id: s.id, paidAt: today() }))}
                    >
                      Mark paid
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(salary ?? []).length === 0 && (
              <TableRow><TableCell colSpan={7}><Typography color="text.secondary" variant="body2">No salary/honor records this masa.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Dialogs ── */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Draft tax deposit — masa {masa}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField select label="Type" value={payType} onChange={(e) => setPayType(e.target.value as TaxPaymentType)}>
              {TAX_PAYMENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{humanizeStatus(t)}</MenuItem>
              ))}
            </TextField>
            <TextField label="Amount" type="number" value={payAmount} onChange={(e) => setPayAmount(Number(e.target.value))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={payAmount <= 0 || createPayment.isPending}
            onClick={() =>
              void run(
                () => createPayment.mutateAsync({ taxType: payType, masa, amount: payAmount }),
                () => setPayOpen(false),
              )
            }
          >
            Create draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(ntpnFor)} onClose={() => setNtpnFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Record NTPN</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              {ntpnFor && `${humanizeStatus(ntpnFor.taxType)} ${ntpnFor.masa} — ${formatIDR(ntpnFor.amount)}`}
            </Typography>
            <TextField label="NTPN" value={ntpn} onChange={(e) => setNtpn(e.target.value)} />
            <TextField
              label="Paid at"
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNtpnFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!ntpn.trim() || markPaid.isPending}
            onClick={() =>
              void run(
                () => markPaid.mutateAsync({ id: (ntpnFor as TaxPayment).id, paidAt, ntpn }),
                () => setNtpnFor(null),
              )
            }
          >
            Mark paid
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bupotOpen} onClose={() => setBupotOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Record received bupot — masa {masa}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField select label="Client" value={bClient} onChange={(e) => setBClient(e.target.value)}>
              {(clients ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Type" value={bType} onChange={(e) => setBType(e.target.value as BupotReceivedType)}>
              <MenuItem value="final_05">0,5% final (Suket)</MenuItem>
              <MenuItem value="pph23">PPh 23 (2%)</MenuItem>
            </TextField>
            <TextField label="Doc number" value={bDoc} onChange={(e) => setBDoc(e.target.value)} />
            <TextField label="DPP" type="number" value={bDpp} onChange={(e) => setBDpp(Number(e.target.value))} />
            <TextField
              label="Received at"
              type="date"
              value={bReceivedAt}
              onChange={(e) => setBReceivedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBupotOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!bClient || !bDoc.trim() || bDpp <= 0 || createBupot.isPending}
            onClick={() =>
              void run(
                () =>
                  createBupot.mutateAsync({
                    clientId: bClient,
                    taxType: bType,
                    docNumber: bDoc,
                    masa,
                    dpp: bDpp,
                    receivedAt: bReceivedAt,
                  }),
                () => setBupotOpen(false),
              )
            }
          >
            Record
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={salaryOpen} onClose={() => setSalaryOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New salary/honor record — masa {masa}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField select label="Person" value={sPerson} onChange={(e) => setSPerson(e.target.value)}>
              {(people ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Type" value={sType} onChange={(e) => setSType(e.target.value as SalaryRecordType)}>
              <MenuItem value="employee_monthly">Employee — TER monthly (Jan–Nov)</MenuItem>
              <MenuItem value="employee_december">Employee — December true-up</MenuItem>
              <MenuItem value="bukan_pegawai">Freelancer — bukan pegawai</MenuItem>
            </TextField>
            <TextField label="Bruto" type="number" value={sBruto} onChange={(e) => setSBruto(Number(e.target.value))} />
            {sType !== 'bukan_pegawai' && (
              <TextField
                label="PTKP status (e.g. TK0) — optional if set on the person"
                value={sPtkp}
                onChange={(e) => setSPtkp(e.target.value)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!sPerson || sBruto <= 0 || createSalary.isPending}
            onClick={() =>
              void run(
                () =>
                  createSalary.mutateAsync({
                    personId: sPerson,
                    type: sType,
                    masa,
                    bruto: sBruto,
                    ...(sPtkp ? { ptkpStatus: sPtkp } : {}),
                  }),
                () => setSalaryOpen(false),
              )
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
